import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Court,
  OrganizationSport,
  PairingPriority,
  PlayEventSeries,
  PlayEventSeriesCreateInput,
  PlayEventSeriesUpdateInput,
  PlayEventSide,
  PlayGenderRestriction,
  PlayJoinPolicy,
  PlayPaymentMode,
  PlaySeriesSchedulePeriodMonths,
  SocialPlayMode,
  OddPlayerPolicy,
} from '@court-sync/shared';
import {
  ODD_PLAYER_POLICIES,
  PLAY_EVENT_SIDES,
  PLAY_GENDER_RESTRICTIONS,
  PLAY_PAYMENT_MODES,
  SOCIAL_PLAY_MODES,
  computeSocialEventDurationMinutes,
  DEFAULT_PAIRING_PRIORITIES,
  formatPlayEventDuration,
  PLAY_SERIES_WEEKDAY_LABELS,
  PLAY_SERIES_SCHEDULE_PERIOD_OPTIONS,
  PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS,
  describePlaySeriesAutoGeneration,
  formatPlaySeriesSchedulePeriod,
  canExtendPlaySeriesSchedule,
  describePlaySeriesEndWarning,
  parsePlaySeriesScheduleEndsAt,
  participantTypesForJoinPolicy,
  playJoinPoliciesForSelect,
} from '@court-sync/shared';
import toast from 'react-hot-toast';
import { Info, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Drawer,
  Field,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { clubService } from '@/services/club';
import { organizationService } from '@/services/organization';
import { playEventService } from '@/services/playEvent';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';
import { PlayEventPairingPrioritiesField } from '@/components/play/PlayEventPairingPrioritiesField';
import {
  PLAY_EVENT_SIDE_LABELS,
  PLAY_GENDER_RESTRICTION_LABELS,
  PLAY_JOIN_POLICY_LABELS,
  PLAY_PAYMENT_MODE_LABELS,
} from '@/lib/playEventLabels';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { PLAY_EVENT_SERIES_CREATE_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { isPageTourComplete } from '@/lib/tours/pageTourCompletion';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';
import { useAuthStore } from '@/store/auth';

const SERIES_FORM_ID = 'play-event-series-form';

const ODD_PLAYER_POLICY_LABELS: Record<OddPlayerPolicy, string> = {
  sit_out: 'Sit out',
  fallback_singles: 'Fallback singles',
  american_singles: 'American singles',
  fallback_doubles_sit_out: 'Doubles sit out',
};

const SOCIAL_PLAY_MODE_LABELS: Record<SocialPlayMode, string> = {
  singles_only: 'Singles only',
  doubles_only: 'Doubles only',
  prefer_doubles: 'Prefer doubles',
};

interface PlayEventSeriesDrawerProps {
  organizationId: string;
  clubId: string;
  timezone: string | null | undefined;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  series?: PlayEventSeries | null;
}

export function PlayEventSeriesDrawer({
  organizationId,
  clubId,
  timezone,
  isOpen,
  onClose,
  onSaved,
  series,
}: PlayEventSeriesDrawerProps) {
  const isEdit = Boolean(series);
  const { user } = useAuthStore();
  const { startPageTour } = usePageTourActions();
  const { hasClubManagement, hasAcademyManagement } = useClubModuleAccess();
  const [drawerPanelOpen, setDrawerPanelOpen] = useState(false);
  const tourScheduledRef = useRef(false);
  const cancelTourScheduleRef = useRef<(() => void) | null>(null);
  const [courts, setCourts] = useState<Court[]>([]);
  const [sports, setSports] = useState<OrganizationSport[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [side, setSide] = useState<PlayEventSide>('doubles');
  const [joinPolicy, setJoinPolicy] = useState<PlayJoinPolicy>('open');
  const [paymentMode, setPaymentMode] = useState<PlayPaymentMode>('at_door');
  const [genderRestriction, setGenderRestriction] = useState<PlayGenderRestriction>('open');
  const [weekday, setWeekday] = useState('2');
  const [startTime, setStartTime] = useState('18:00');
  const [roundDurationMinutes, setRoundDurationMinutes] = useState('30');
  const [roundBreakMinutes, setRoundBreakMinutes] = useState('0');
  const [courtIds, setCourtIds] = useState<string[]>([]);
  const [organizationSportId, setOrganizationSportId] = useState('');
  const [socialPlayMode, setSocialPlayMode] = useState<SocialPlayMode>('prefer_doubles');
  const [oddPlayerPolicy, setOddPlayerPolicy] = useState<OddPlayerPolicy>('sit_out');
  const [roundCount, setRoundCount] = useState('4');
  const [pairingPriorities, setPairingPriorities] = useState<PairingPriority[]>([
    ...DEFAULT_PAIRING_PRIORITIES,
  ]);
  const [isActive, setIsActive] = useState(true);
  const [kioskRegistrationEnabled, setKioskRegistrationEnabled] = useState(false);
  const [kioskGuestRegistrationEnabled, setKioskGuestRegistrationEnabled] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [matchScoringEnabled, setMatchScoringEnabled] = useState(false);
  const [schedulePeriodMonths, setSchedulePeriodMonths] = useState<PlaySeriesSchedulePeriodMonths>(
    PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS
  );

  const resetFromSeries = (value: PlayEventSeries) => {
    setName(value.name);
    setSide(value.side);
    setJoinPolicy(value.joinPolicy);
    setPaymentMode(value.paymentMode);
    setGenderRestriction(value.genderRestriction ?? 'open');
    setWeekday(String(value.weekday ?? 2));
    setStartTime(value.defaultStartTime ?? '18:00');
    setRoundDurationMinutes(String(value.roundDurationMinutes ?? 30));
    setRoundBreakMinutes(String(value.roundBreakMinutes ?? 0));
    setCourtIds(value.defaultCourtIds ?? []);
    setSocialPlayMode(value.socialPlayMode ?? 'prefer_doubles');
    setOddPlayerPolicy(value.oddPlayerPolicy ?? 'sit_out');
    setRoundCount(String(value.roundCount ?? 4));
    setPairingPriorities(value.pairingPriorities ?? [...DEFAULT_PAIRING_PRIORITIES]);
    setIsActive(value.isActive);
    setKioskRegistrationEnabled(value.kioskRegistrationEnabled);
    setKioskGuestRegistrationEnabled(value.kioskGuestRegistrationEnabled);
    setIsFeatured(value.isFeatured);
    setMatchScoringEnabled(value.matchScoringEnabled);
    setSchedulePeriodMonths(
      value.schedulePeriodMonths ?? PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS
    );
    setOrganizationSportId(value.academySportId ?? '');
  };

  const resetCreate = () => {
    setName('');
    setSide('doubles');
    setJoinPolicy('open');
    setPaymentMode('at_door');
    setGenderRestriction('open');
    setWeekday('2');
    setStartTime('18:00');
    setRoundDurationMinutes('30');
    setRoundBreakMinutes('0');
    setCourtIds([]);
    setSocialPlayMode('prefer_doubles');
    setOddPlayerPolicy('sit_out');
    setRoundCount('4');
    setPairingPriorities([...DEFAULT_PAIRING_PRIORITIES]);
    setIsActive(true);
    setKioskRegistrationEnabled(false);
    setKioskGuestRegistrationEnabled(false);
    setIsFeatured(false);
    setMatchScoringEnabled(true);
    setSchedulePeriodMonths(PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    if (series) {
      resetFromSeries(series);
    } else {
      resetCreate();
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [courtsRes, sportsRes] = await Promise.all([
          clubService.getClubCourts(clubId),
          organizationService.getOrganizationSports(organizationId),
        ]);
        if (cancelled) return;
        const courtList = courtsRes.success && courtsRes.data ? courtsRes.data : [];
        const sportList =
          sportsRes.success && sportsRes.data
            ? sportsRes.data.filter((sport) => sport.isActive)
            : [];
        setCourts(courtList);
        setSports(sportList);
        if (!series) {
          const primary = sportList.find((sport) => sport.isPrimary) ?? sportList[0];
          setOrganizationSportId(primary?.id ?? '');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [clubId, isOpen, organizationId, series]);

  const derivedDurationLabel = useMemo(() => {
    const rounds = Number(roundCount);
    const roundMin = Number(roundDurationMinutes);
    const breakMin = Number(roundBreakMinutes);
    if (
      !Number.isFinite(rounds) ||
      rounds < 1 ||
      !Number.isFinite(roundMin) ||
      roundMin < 5 ||
      !Number.isFinite(breakMin) ||
      breakMin < 0
    ) {
      return null;
    }
    const totalMinutes = computeSocialEventDurationMinutes(rounds, roundMin, breakMin);
    if (totalMinutes < 1) return null;
    return formatPlayEventDuration(totalMinutes);
  }, [roundBreakMinutes, roundCount, roundDurationMinutes]);

  const selectedSport = sports.find((sport) => sport.id === organizationSportId);

  const toggleCourt = (courtId: string) => {
    setCourtIds((prev) =>
      prev.includes(courtId) ? prev.filter((id) => id !== courtId) : [...prev, courtId]
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedSport) {
      toast.error('Select a sport for this series');
      return;
    }
    if (!name.trim()) {
      toast.error('Series name is required');
      return;
    }
    if (courtIds.length === 0) {
      toast.error('Select at least one court');
      return;
    }

    const rounds = Number(roundCount);
    const roundMin = Number(roundDurationMinutes);
    const breakMin = Number(roundBreakMinutes);
    if (!Number.isFinite(rounds) || rounds < 1 || rounds > 50) {
      toast.error('Enter a valid number of rounds');
      return;
    }
    if (!Number.isFinite(roundMin) || roundMin < 5 || roundMin > 240) {
      toast.error('Round duration must be between 5 and 240 minutes');
      return;
    }
    if (!Number.isFinite(breakMin) || breakMin < 0 || breakMin > 120) {
      toast.error('Break duration must be between 0 and 120 minutes');
      return;
    }
    const defaultDurationMinutes = computeSocialEventDurationMinutes(rounds, roundMin, breakMin);
    if (defaultDurationMinutes < 30) {
      toast.error('Session duration must be at least 30 minutes');
      return;
    }

    const weekdayNum = Number(weekday);
    if (!Number.isFinite(weekdayNum) || weekdayNum < 0 || weekdayNum > 6) {
      toast.error('Select a weekday');
      return;
    }

    const basePayload = {
      name: name.trim(),
      sportId: selectedSport.sportId,
      academySportId: selectedSport.id,
      format: 'social_rotate' as const,
      side,
      joinPolicy,
      paymentMode,
      genderRestriction,
      timezone: timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      pairingPriorities,
      matchupVisibilityMinutes: 15,
      nextMatchWithdrawMinutes: 10,
      memberPriceCents: null,
      studentPriceCents: null,
      guestPriceCents: null,
      socialPlayMode,
      oddPlayerPolicy,
      roundCount: rounds,
      roundDurationMinutes: roundMin,
      roundBreakMinutes: breakMin,
      defaultCourtIds: courtIds,
      weekday: weekdayNum,
      defaultStartTime: startTime,
      defaultDurationMinutes,
      isActive,
      kioskRegistrationEnabled,
      kioskGuestRegistrationEnabled: kioskRegistrationEnabled
        ? kioskGuestRegistrationEnabled
        : false,
      isFeatured,
      matchScoringEnabled,
      schedulePeriodMonths,
    };

    setSubmitting(true);
    try {
      if (isEdit && series) {
        const payload: PlayEventSeriesUpdateInput = basePayload;
        const res = await playEventService.updateSeries(organizationId, series.id, payload);
        if (res.success) {
          toast.success('Series updated');
          onSaved?.();
          onClose();
          return;
        }
        toast.error(res.error ?? 'Failed to update series');
        return;
      }

      const payload: PlayEventSeriesCreateInput = basePayload;
      const res = await playEventService.createSeries(organizationId, payload);
      if (res.success) {
        toast.success(
          `Series created. Published events for the next ${formatPlaySeriesSchedulePeriod(schedulePeriodMonths)} will appear shortly.`
        );
        onSaved?.();
        onClose();
        return;
      }
      toast.error(res.error ?? 'Failed to create series');
    } finally {
      setSubmitting(false);
    }
  };

  const scheduleEndsAt = series?.scheduleEndsAt;
  const joinPolicyAllowsGuests = participantTypesForJoinPolicy(joinPolicy).includes('guest');
  const availableJoinPolicies = useMemo(
    () =>
      playJoinPoliciesForSelect(
        { hasClubManagement, hasAcademyManagement },
        joinPolicy
      ),
    [hasClubManagement, hasAcademyManagement, joinPolicy]
  );
  const effectiveSchedulePeriodMonths =
    series?.schedulePeriodMonths ?? schedulePeriodMonths ?? PLAY_SERIES_DEFAULT_SCHEDULE_PERIOD_MONTHS;
  const showScheduleEndWarning =
    isEdit && series != null && canExtendPlaySeriesSchedule(series);

  const handleExtendSchedule = async () => {
    if (!series) return;
    setSubmitting(true);
    try {
      const res = await playEventService.extendSeriesSchedule(organizationId, series.id);
      if (res.success) {
        const created = res.data?.createdEventIds?.length ?? 0;
        toast.success(
          created > 0
            ? `Schedule extended for another ${formatPlaySeriesSchedulePeriod()}. ${created} new event${created === 1 ? '' : 's'} created.`
            : `Schedule extended for another ${formatPlaySeriesSchedulePeriod()}.`
        );
        onSaved?.();
        onClose();
      } else {
        toast.error(res.error ?? 'Failed to extend schedule');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivateSeries = async () => {
    if (!series) return;
    setSubmitting(true);
    try {
      const res = await playEventService.updateSeries(organizationId, series.id, { isActive: false });
      if (res.success) {
        toast.success('Series deactivated');
        onSaved?.();
        onClose();
      } else {
        toast.error(res.error ?? 'Failed to deactivate series');
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setDrawerPanelOpen(false);
      tourScheduledRef.current = false;
      cancelTourScheduleRef.current?.();
      cancelTourScheduleRef.current = null;
    }
  }, [isOpen]);

  const handleDrawerPanelOpen = useCallback(() => {
    setDrawerPanelOpen(true);
    if (
      isEdit ||
      !isOpen ||
      tourScheduledRef.current ||
      isPageTourComplete(user?.preferences, 'play-events-series-create')
    ) {
      return;
    }
    tourScheduledRef.current = true;
    cancelTourScheduleRef.current?.();
    cancelTourScheduleRef.current = scheduleAutoPageTour(
      startPageTour,
      'play-events-series-create',
      user?.preferences,
    );
  }, [isEdit, isOpen, startPageTour, user?.preferences]);

  usePageTourReady(
    'play-events-series-create',
    isOpen && !isEdit && drawerPanelOpen && !loading,
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit play series' : 'New play series'}
      description="Weekly social play template."
      width="2xl"
      panelId={!isEdit ? PLAY_EVENT_SERIES_CREATE_TOUR_STEP_IDS.DRAWER : undefined}
      onPanelOpen={!isEdit ? handleDrawerPanelOpen : undefined}
      headerActions={
        !isEdit ? (
          <DrawerTourHelp
            tourKey="play-events-series-create"
            suggestion="Creating a weekly series? Tour this form for schedule, pairing, and auto-generated sessions."
            buttonLabel="Tour this form"
          />
        ) : undefined
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            id={!isEdit ? PLAY_EVENT_SERIES_CREATE_TOUR_STEP_IDS.SUBMIT : undefined}
            type="submit"
            form={SERIES_FORM_ID}
            isLoading={submitting}
            disabled={loading || courts.length === 0}
          >
            {isEdit ? 'Save series' : 'Create series'}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex min-h-[200px] items-center justify-center" aria-busy="true">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
        </div>
      ) : (
        <form id={SERIES_FORM_ID} className="space-y-6" onSubmit={(e) => void handleSubmit(e)}>
          {showScheduleEndWarning && scheduleEndsAt ? (
            <div
              className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
              role="alert"
            >
              <AlertTriangle
                className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400"
                aria-hidden
              />
              <div className="space-y-3">
                <p>
                  {describePlaySeriesEndWarning(
                    scheduleEndsAt,
                    series?.timezone,
                    effectiveSchedulePeriodMonths
                  )}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={submitting}
                    onClick={() => void handleExtendSchedule()}
                  >
                    Extend {formatPlaySeriesSchedulePeriod(effectiveSchedulePeriodMonths)}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={submitting}
                    onClick={() => void handleDeactivateSeries()}
                  >
                    Deactivate series
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          <div id={PLAY_EVENT_SERIES_CREATE_TOUR_STEP_IDS.SCHEDULE} className="space-y-6">
          <Field>
            <FieldLabel htmlFor="play-series-name">Name</FieldLabel>
            <Input
              id="play-series-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tuesday night social"
              required
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field>
              <FieldLabel>Weekday</FieldLabel>
              <Select value={weekday} onValueChange={setWeekday}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAY_SERIES_WEEKDAY_LABELS.map((label, index) => (
                    <SelectItem key={label} value={String(index)}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="play-series-start-time">Start time</FieldLabel>
              <Input
                id="play-series-start-time"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel>Side</FieldLabel>
              <Select value={side} onValueChange={(v) => setSide(v as PlayEventSide)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAY_EVENT_SIDES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {PLAY_EVENT_SIDE_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          </div>

          <div id={PLAY_EVENT_SERIES_CREATE_TOUR_STEP_IDS.SOCIAL} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel>Social mode</FieldLabel>
              <Select
                value={socialPlayMode}
                onValueChange={(v) => setSocialPlayMode(v as SocialPlayMode)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOCIAL_PLAY_MODES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {SOCIAL_PLAY_MODE_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Odd players</FieldLabel>
              <Select
                value={oddPlayerPolicy}
                onValueChange={(v) => setOddPlayerPolicy(v as OddPlayerPolicy)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ODD_PLAYER_POLICIES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {ODD_PLAYER_POLICY_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <PlayEventPairingPrioritiesField
            value={pairingPriorities}
            onChange={setPairingPriorities}
          />
          </div>

          <div id={PLAY_EVENT_SERIES_CREATE_TOUR_STEP_IDS.ROUNDS} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field>
              <FieldLabel htmlFor="play-series-rounds">Rounds</FieldLabel>
              <Input
                id="play-series-rounds"
                type="number"
                min={1}
                max={50}
                value={roundCount}
                onChange={(e) => setRoundCount(e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="play-series-round-duration">Round duration (min)</FieldLabel>
              <Input
                id="play-series-round-duration"
                type="number"
                min={5}
                max={240}
                step={5}
                value={roundDurationMinutes}
                onChange={(e) => setRoundDurationMinutes(e.target.value)}
                required
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="play-series-round-break">Break between rounds (min)</FieldLabel>
              <Input
                id="play-series-round-break"
                type="number"
                min={0}
                max={120}
                step={1}
                value={roundBreakMinutes}
                onChange={(e) => setRoundBreakMinutes(e.target.value)}
                required
              />
            </Field>
          </div>

          {derivedDurationLabel ? (
            <p className="text-sm text-muted-foreground">
              Each session lasts {derivedDurationLabel} (rounds + breaks).
            </p>
          ) : null}
          </div>

          <div id={PLAY_EVENT_SERIES_CREATE_TOUR_STEP_IDS.REGISTRATION} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field>
              <FieldLabel>Join policy</FieldLabel>
              <Select value={joinPolicy} onValueChange={(v) => setJoinPolicy(v as PlayJoinPolicy)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableJoinPolicies.map((value) => (
                    <SelectItem key={value} value={value}>
                      {PLAY_JOIN_POLICY_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Payment</FieldLabel>
              <Select
                value={paymentMode}
                onValueChange={(v) => setPaymentMode(v as PlayPaymentMode)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAY_PAYMENT_MODES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {PLAY_PAYMENT_MODE_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Gender restriction</FieldLabel>
              <Select
                value={genderRestriction}
                onValueChange={(v) => setGenderRestriction(v as PlayGenderRestriction)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAY_GENDER_RESTRICTIONS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {PLAY_GENDER_RESTRICTION_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {sports.length > 1 ? (
            <Field>
              <FieldLabel>Sport</FieldLabel>
              <Select value={organizationSportId} onValueChange={setOrganizationSportId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sports.map((sport) => (
                    <SelectItem key={sport.id} value={sport.id}>
                      {sport.customLabel ?? sport.sportId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ) : null}
          </div>

          <div id={PLAY_EVENT_SERIES_CREATE_TOUR_STEP_IDS.COURTS}>
          <Field>
            <FieldLabel>Courts</FieldLabel>
            <div className="grid gap-2 sm:grid-cols-2">
              {courts.map((court) => (
                <label
                  key={court.id}
                  className="flex items-center gap-2 rounded-md border border-border p-3 text-sm"
                >
                  <Checkbox
                    checked={courtIds.includes(court.id)}
                    onCheckedChange={() => toggleCourt(court.id)}
                  />
                  <span>{court.name}</span>
                </label>
              ))}
            </div>
          </Field>
          </div>

          <div id={PLAY_EVENT_SERIES_CREATE_TOUR_STEP_IDS.GENERATION} className="space-y-6">
          <div
            className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50/50 p-4 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200"
            role="note"
          >
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
            <p>{describePlaySeriesAutoGeneration(schedulePeriodMonths)}</p>
          </div>

          <Field>
            <FieldLabel>Schedule period</FieldLabel>
            <Select
              value={String(schedulePeriodMonths)}
              onValueChange={(value) =>
                setSchedulePeriodMonths(Number(value) as PlaySeriesSchedulePeriodMonths)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLAY_SERIES_SCHEDULE_PERIOD_OPTIONS.map((months) => (
                  <SelectItem key={months} value={String(months)}>
                    {formatPlaySeriesSchedulePeriod(months)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {isEdit
                ? 'Used when you extend the schedule. Changing this does not move the current end date.'
                : 'How far ahead published sessions are created while the series stays active.'}
            </p>
          </Field>

          {isEdit ? (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={isActive} onCheckedChange={(v) => setIsActive(Boolean(v))} />
              <span>
                Active — auto-creates published events for the next{' '}
                {formatPlaySeriesSchedulePeriod(effectiveSchedulePeriodMonths)}
                {scheduleEndsAt
                  ? ` (through ${format(parsePlaySeriesScheduleEndsAt(scheduleEndsAt), 'd MMM yyyy')})`
                  : ''}
              </span>
            </label>
          ) : null}

          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={isFeatured}
              onCheckedChange={(v) => setIsFeatured(Boolean(v))}
            />
            <span>Feature on public home page</span>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={matchScoringEnabled}
              onCheckedChange={(v) => setMatchScoringEnabled(Boolean(v))}
            />
            <span>
              Record match scores
              <span className="mt-1 block text-muted-foreground">
                When enabled, operators can record results and set scores from the match draw.
              </span>
            </span>
          </label>

          {(hasClubManagement || hasAcademyManagement) ? (
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  className="mt-0.5"
                  checked={kioskRegistrationEnabled}
                  onCheckedChange={(v) => {
                    const enabled = Boolean(v);
                    setKioskRegistrationEnabled(enabled);
                    if (!enabled) {
                      setKioskGuestRegistrationEnabled(false);
                    }
                  }}
                />
                <span>
                  Allow kiosk registration
                  <span className="mt-1 block text-muted-foreground">
                    {hasClubManagement
                      ? 'Members and students can register on the day at the event kiosk. Ideal for social play where last-minute drop-ins should not disrupt the roster.'
                      : 'Students can register on the day at the event kiosk. Ideal for social play where last-minute drop-ins should not disrupt the roster.'}
                  </span>
                </span>
              </label>
              {kioskRegistrationEnabled && joinPolicyAllowsGuests ? (
                <label className="ml-7 flex items-start gap-2 text-sm">
                  <Checkbox
                    className="mt-0.5"
                    checked={kioskGuestRegistrationEnabled}
                    onCheckedChange={(v) => setKioskGuestRegistrationEnabled(Boolean(v))}
                  />
                  <span>
                    Allow guest registration at kiosk
                    <span className="mt-1 block text-muted-foreground">
                      Walk-in guests can register and check in from the kiosk when this is enabled.
                    </span>
                  </span>
                </label>
              ) : null}
            </div>
          ) : null}

          {isEdit && series?.lastGenerationSummary ? (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Last generation</p>
              <p>
                Created {series.lastGenerationSummary.createdCount} event
                {series.lastGenerationSummary.createdCount === 1 ? '' : 's'}
                {series.lastGenerationSummary.skipped.length > 0
                  ? ` · ${series.lastGenerationSummary.skipped.length} skipped`
                  : ''}
                {series.lastGenerationSummary.membersAutoEnrolled != null
                  ? ` · ${series.lastGenerationSummary.membersAutoEnrolled} member${
                      series.lastGenerationSummary.membersAutoEnrolled === 1 ? '' : 's'
                    } auto-registered`
                  : ''}
                {series.lastGenerationSummary.studentsAutoEnrolled != null
                  ? ` · ${series.lastGenerationSummary.studentsAutoEnrolled} student${
                      series.lastGenerationSummary.studentsAutoEnrolled === 1 ? '' : 's'
                    } auto-registered`
                  : ''}
              </p>
            </div>
          ) : null}

          {isEdit && series ? (
            <Button variant="outline" asChild className="w-full sm:w-auto">
              <Link to="/admin/calendar">View on calendar</Link>
            </Button>
          ) : null}
          </div>
        </form>
      )}
    </Drawer>
  );
}
